import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShoppingBag, TrendingUp, AlertCircle } from 'lucide-react';
import { devError } from '../../lib/devLog';
import { adminFetch, adminFetchJson } from '../../lib/adminAuth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v, ccy = 'GBP') => (
  typeof v === 'number'
    ? `${ccy.toUpperCase() === 'GBP' ? '£' : ccy + ' '}${v.toLocaleString('en-GB')}`
    : '—'
);

const STATUS_PILL = {
  paid:       { label: 'Paid',      cls: 'bg-green-100 text-green-800' },
  initiated:  { label: 'Started',   cls: 'bg-amber-100 text-amber-800' },
  expired:    { label: 'Expired',   cls: 'bg-neutral-100 text-neutral-600' },
  cancelled:  { label: 'Cancelled', cls: 'bg-neutral-100 text-neutral-600' },
};

/**
 * Rolling 7-day buy-flow conversion widget for the admin dashboard.
 * Renders started / completed / abandoned counts plus the most recent
 * 5 transactions. Lets Gabrielle follow up with anyone who abandoned
 * — the buyer email is always captured even if checkout never finished.
 */
const ShippingLinkButton = ({ row, onSent }) => {
  const [busy, setBusy] = useState(false);
  if (row.payment_status !== 'paid') return null;
  if (row.shipping_paid) {
    return <span className="text-[10px] uppercase tracking-wider text-green-700" data-testid={`bf-shipping-paid-${row.session_id}`}>Shipping paid</span>;
  }
  if (row.metadata?.shipping) return null;
  const send = async () => {
    const intl = row.metadata?.delivery_region !== 'UK';
    const ok = window.confirm(
      `Email ${row.buyer_email} a secure link to pay ${intl ? 'international shipping (£95)' : 'UK shipping (£35 + VAT)'}?`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await adminFetchJson(`${BACKEND_URL}/api/admin/payments/${row.session_id}/shipping-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ send_email: true }),
      });
      toast.success(res.emailed ? `Shipping link emailed to ${row.buyer_email}` : 'Link created — email could not be sent');
      onSent();
    } catch (err) {
      toast.error(`Could not create shipping link: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={send}
      disabled={busy}
      className="mt-1 text-[11px] px-2.5 py-1 rounded border border-gray-300 hover:bg-black hover:text-white transition-colors disabled:opacity-50"
      data-testid={`bf-send-shipping-${row.session_id}`}
    >
      {busy ? 'Sending…' : row.shipping_link_sent_at ? 'Resend shipping link' : 'Send shipping link'}
    </button>
  );
};

const ResendReceiptButton = ({ row, onSent }) => {
  const [busy, setBusy] = useState(false);
  if (row.payment_status !== 'paid' || row.shipping_paid || row.metadata?.shipping) return null;
  const send = async () => {
    if (!window.confirm(`Re-send the receipt to ${row.buyer_email} with a note that shipping wasn't paid and a pay-shipping button?`)) return;
    setBusy(true);
    try {
      const res = await adminFetchJson(`${BACKEND_URL}/api/admin/payments/${row.session_id}/resend-receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      toast.success(res.emailed ? `Receipt re-sent to ${row.buyer_email}` : 'Link created — email could not be sent');
      onSent();
    } catch (err) {
      toast.error(`Could not resend receipt: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={send}
      disabled={busy}
      className="mt-1 ml-1 text-[11px] px-2.5 py-1 rounded border border-gray-300 hover:bg-black hover:text-white transition-colors disabled:opacity-50"
      data-testid={`bf-resend-receipt-${row.session_id}`}
    >
      {busy ? 'Sending…' : row.receipt_resent_at ? 'Receipt re-sent · send again' : 'Resend receipt + shipping note'}
    </button>
  );
};

const downloadLedger = async () => {
  try {
    const res = await adminFetch(`${BACKEND_URL}/api/admin/payments/ledger.csv`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `zebra-one-sales-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(`Could not download spreadsheet: ${err.message}`);
  }
};

const BuyFlowWidget = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/admin/payments/buy-flow-stats`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((err) => {
        devError('Buy-flow widget fetch failed', err);
        setError(true);
      });
  }, [reload]);

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-2 text-gray-500">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">Buy-flow stats unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2"
      data-testid="buy-flow-widget"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5" />
          <h2 className="text-xl font-semibold">Buy-Flow · Last 7 Days</h2>
        </div>
        {data && data.started > 0 && (
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <TrendingUp className="w-3.5 h-3.5" />
            <span data-testid="buy-flow-conversion">
              {data.conversion_rate}% conversion
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Stat label="Started"   value={data ? data.started   : '…'} testid="bf-started"   color="bg-blue-50 text-blue-700" />
        <Stat label="Completed" value={data ? data.completed : '…'} testid="bf-completed" color="bg-green-50 text-green-700" />
        <Stat label="Abandoned" value={data ? data.abandoned : '…'} testid="bf-abandoned" color="bg-amber-50 text-amber-700" />
      </div>

      {/* Recent transactions list */}
      <div className="border-t border-gray-100 pt-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-wider text-gray-400">Recent orders</span>
          <button
            type="button"
            onClick={downloadLedger}
            className="text-[11px] px-2.5 py-1 rounded border border-gray-300 hover:bg-black hover:text-white transition-colors"
            data-testid="bf-download-ledger"
          >
            Download sales spreadsheet
          </button>
        </div>
        {!data && <p className="text-sm text-gray-400">Loading…</p>}
        {data && data.recent.length === 0 && (
          <p className="text-sm text-gray-500">No buy-flow activity yet — when a visitor clicks Buy on an artwork it will show up here.</p>
        )}
        {data && data.recent.map((r) => {
          const pill = STATUS_PILL[r.payment_status] || STATUS_PILL[r.status] || { label: r.status || '—', cls: 'bg-neutral-100 text-neutral-600' };
          return (
            <div
              key={r.session_id}
              className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0"
              data-testid={`bf-row-${r.session_id}`}
            >
              <div className="min-w-0 flex-1 pr-4">
                <p className="text-sm font-medium truncate">
                  {r.artwork_title || 'Untitled'}
                  {r.artist_name && <span className="text-gray-500 font-normal"> · {r.artist_name}</span>}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {r.buyer_name ? `${r.buyer_name} ` : ''}<span className="text-gray-400">&lt;{r.buyer_email}&gt;</span>
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-medium">{fmtMoney(r.amount, r.currency)}</p>
                <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${pill.cls}`}>
                  {pill.label}
                </span>
                {r.reminder_sent_at && r.payment_status !== 'paid' && (
                  <div className="text-[10px] uppercase tracking-wider text-gray-400" data-testid={`bf-reminder-sent-${r.session_id}`}>Reminder sent</div>
                )}
                <div><ShippingLinkButton row={r} onSent={() => setReload((n) => n + 1)} /><ResendReceiptButton row={r} onSent={() => setReload((n) => n + 1)} /></div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

const Stat = ({ label, value, color, testid }) => (
  <div className={`rounded-lg p-4 ${color}`} data-testid={testid}>
    <p className="text-3xl font-bold leading-none">{value}</p>
    <p className="text-xs uppercase tracking-wider mt-2 opacity-70">{label}</p>
  </div>
);

export default BuyFlowWidget;
